import { format } from 'date-fns';
import type { MemberDataset, ProvidersKey } from './memberDataset.types';
import { formatFieldValue, type MemberViewFormatters, parseDateOnly } from './memberViewFormat';

/** The translated pieces a CSV needs, resolved by the caller so this module stays pure. */
export interface CsvText {
  header: (label: ProvidersKey) => string;
  yes: string;
  no: string;
}

const BOM = '﻿';

/**
 * A spreadsheet runs a cell starting with one of these as a formula. Tab and line breaks too: a
 * spreadsheet may strip them and evaluate the `=` behind them.
 */
const FORMULA_START = /^[=+\-@\t\r\n]/;

/**
 * The report keeps legacy's value formats, not the table's locale ones: dates as M/d/yyyy, numbers
 * raw and currency as "$" plus the raw amount (no grouping, no fixed decimals), so the columns
 * stay numeric when the file is opened in a spreadsheet.
 */
function csvFormatters(text: CsvText): MemberViewFormatters {
  return {
    date: (dateOnly) => {
      const parsed = parseDateOnly(dateOnly);
      return parsed ? format(parsed, 'M/d/yyyy') : '';
    },
    currency: (amount) => `$${amount}`,
    number: (n) => String(n),
    yes: text.yes,
    no: text.no,
  };
}

/** Quotes every cell and guards it against formula injection, since the file is opened in Excel. */
function csvCell(value: string): string {
  const guarded = FORMULA_START.test(value) ? `'${value}` : value;
  return `"${guarded.replace(/"/g, '""')}"`;
}

/**
 * A dataset's rows as a CSV file's contents: its CSV fields in `csv.order`, a header line of their
 * CSV labels, then one line per row in the order given. Starts with a UTF-8 BOM so Excel doesn't
 * misread accented names; lines end in CRLF.
 */
export function buildCsv<TRow>(rows: TRow[], fields: MemberDataset<TRow>['fields'], text: CsvText): string {
  const columns = fields
    .flatMap((field) => (field.csv ? [{ field, csv: field.csv }] : []))
    .sort((a, b) => a.csv.order - b.csv.order);
  const formatters = csvFormatters(text);

  const header = columns.map(({ csv }) => csvCell(text.header(csv.label))).join(',');
  const lines = rows.map((row) =>
    columns.map(({ field }) => csvCell(formatFieldValue(field.value(row), field.format, formatters))).join(','),
  );

  return BOM + [header, ...lines].join('\r\n');
}
